import { Link } from 'react-router-dom';
import type { Employee, TimelineEvent } from '../api/types';
import { diagnosticTypeLabel, fileFormatLabel } from '../domain/diagnostics';
import { eventTypeLabel, interventionCategoryLabel } from '../domain/events';
import { partOperationLabel } from '../domain/parts';
import { workstationStateLabel } from '../domain/workstations';
import { formatDateTime, fullName } from '../lib/format';
import { Badge } from './Badge';

function readString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

function readNumber(payload: Record<string, unknown>, key: string): number | null {
  const value = payload[key];
  return typeof value === 'number' ? value : null;
}

/** Détail lisible d'un événement, selon son type. */
function EventDetails({
  event,
  employeeNames,
}: {
  event: TimelineEvent;
  employeeNames: Map<string, string>;
}) {
  if (event.type === 'changementProprietaire') {
    const employeeId = readString(event.payload, 'salarieId');
    if (employeeId === null) {
      return <span className="doux">Retour au parc, sans détenteur.</span>;
    }
    return (
      <span>
        Nouveau propriétaire :{' '}
        <strong>{employeeNames.get(employeeId) ?? 'salarié inconnu'}</strong>
      </span>
    );
  }

  if (event.type === 'changementEtat') {
    const state = readString(event.payload, 'etat');
    return (
      <span>
        Nouvel état : <strong>{workstationStateLabel(state)}</strong>
      </span>
    );
  }

  if (event.type === 'changementPiece') {
    const operation = readString(event.payload, 'operation');
    const decrire = (cle: 'montee' | 'retiree'): string | null => {
      const bloc = event.payload[cle];
      if (bloc === null || typeof bloc !== 'object') return null;
      const descripteur = bloc as Record<string, unknown>;
      const exemplaire = readString(descripteur, 'exemplaireId');
      if (exemplaire !== null) return `exemplaire ${exemplaire.slice(0, 8)}`;
      const quantite = readNumber(descripteur, 'quantite');
      return quantite === null ? null : `${quantite} pièce(s)`;
    };

    const montee = decrire('montee');
    const retiree = decrire('retiree');
    return (
      <span>
        <Badge tone="accent">{partOperationLabel(operation ?? '')}</Badge>{' '}
        {retiree !== null && <span>retiré : {retiree}</span>}
        {retiree !== null && montee !== null && <span className="faible"> · </span>}
        {montee !== null && <span>monté : {montee}</span>}
      </span>
    );
  }

  if (event.type === 'intervention') {
    const category = readString(event.payload, 'categorie');
    const description = readString(event.payload, 'description');
    const minutes = readNumber(event.payload, 'dureeMinutes');
    return (
      <span>
        {category !== null && <Badge tone="accent">{interventionCategoryLabel(category)}</Badge>}{' '}
        {description ?? ''}
        {minutes !== null && <span className="faible"> · {minutes} min</span>}
      </span>
    );
  }

  if (event.type === 'miseAuRebut') {
    const destination = readString(event.payload, 'destination');
    return destination === null ? null : <span>Destination : {destination}</span>;
  }

  if (event.type === 'annulation' && event.cancels !== null) {
    return (
      <span className="doux">
        Annule l'événement {eventTypeLabel(event.cancels.type)} du{' '}
        {formatDateTime(event.cancels.occurredAt)}.
      </span>
    );
  }

  if (event.type === 'importDiagnostic' && event.diagnostic !== null) {
    return (
      <span>
        <Link to={`/diagnostics/${event.diagnostic.id}`}>
          {diagnosticTypeLabel(event.diagnostic.type)}
        </Link>{' '}
        <span className="faible">
          · {fileFormatLabel(event.diagnostic.fileFormat)} ·{' '}
          {event.diagnostic.originalFilename}
        </span>
      </span>
    );
  }

  return null;
}

export function Timeline({
  events,
  employees,
  canCancel,
  onCancel,
}: {
  events: TimelineEvent[];
  employees: Employee[];
  canCancel: boolean;
  onCancel: (event: TimelineEvent) => void;
}) {
  const employeeNames = new Map(
    employees.map((employee) => [employee.id, fullName(employee)]),
  );

  if (events.length === 0) {
    return <div className="tableau__vide">Aucun événement pour ce poste.</div>;
  }

  return (
    <ol className="frise">
      {events.map((event) => (
        <li
          key={event.id}
          className={event.cancelled ? 'frise__entree frise__entree--annule' : 'frise__entree'}
        >
          <div className="frise__jalon" aria-hidden="true" />
          <div className="frise__corps">
            <div className="frise__titre">
              <strong>{eventTypeLabel(event.type)}</strong>
              <span className="mono doux">{formatDateTime(event.occurredAt)}</span>
              {event.cancelled && <Badge tone="danger">Annulé</Badge>}
              {canCancel && !event.cancelled && event.type !== 'annulation' && (
                <button
                  type="button"
                  className="bouton bouton--fantome bouton--petit pousse"
                  onClick={() => onCancel(event)}
                >
                  Annuler cet événement
                </button>
              )}
            </div>

            <div className="frise__meta">
              Par {event.author.displayName} · enregistré le{' '}
              {formatDateTime(event.recordedAt)}
            </div>

            <div style={{ marginTop: 'var(--espace-3)' }}>
              <EventDetails event={event} employeeNames={employeeNames} />
            </div>

            {event.reason !== '' && <div className="frise__motif">{event.reason}</div>}

            {event.cancellation !== null && (
              <div className="frise__meta">
                Annulé par {event.cancellation.author} le{' '}
                {formatDateTime(event.cancellation.occurredAt)}
                {event.cancellation.reason !== '' && ` — ${event.cancellation.reason}`}
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
